/**
 * Decode a design PNG to an alpha plane for `proposeAlignment` — browser only (canvas).
 *
 * Kept out of `design-align-lib` so the geometry stays importable under jsdom, which has no canvas.
 * Runs against the `File` the browser already holds, BEFORE the upload: the preview WebP does not
 * exist at create time (a worker renders it after the S3 upload, and the library polls up to three
 * minutes for it), so decoding the local file is the only way to propose a box during the create flow
 * — and it sidesteps CORS on the CDN entirely (plan § 4.1).
 */

export interface DecodedArt {
  /** One byte per pixel of the DOWNSCALED raster. */
  alpha: Uint8Array;
  widthPx: number;
  heightPx: number;
  /** The file's real pixel size, which is what the print inches come from. */
  naturalWidthPx: number;
  naturalHeightPx: number;
}

/** Canonical print resolution: 1 print inch = 300 px. Mirrors the API's `PRINT_DPI`. */
export const PRINT_DPI = 300;

/**
 * A 16 × 20 in design at 300 DPI is ~28 MP; decoding that at full size on a shop iPad is wasteful and
 * tells the profile nothing extra. A 1000-px longest edge still resolves ⅛ in on a 20 in file.
 */
export const DECODE_MAX_EDGE = 1000;

export async function decodeArtForAlignment(
  file: Blob,
  maxEdge = DECODE_MAX_EDGE,
): Promise<DecodedArt | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const naturalWidthPx = bitmap.width;
    const naturalHeightPx = bitmap.height;
    const scale = Math.min(1, maxEdge / Math.max(naturalWidthPx, naturalHeightPx));
    const widthPx = Math.max(1, Math.round(naturalWidthPx * scale));
    const heightPx = Math.max(1, Math.round(naturalHeightPx * scale));
    const canvas = document.createElement('canvas');
    canvas.width = widthPx;
    canvas.height = heightPx;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, widthPx, heightPx);
    const { data } = ctx.getImageData(0, 0, widthPx, heightPx);
    const alpha = new Uint8Array(widthPx * heightPx);
    for (let i = 0; i < alpha.length; i++) alpha[i] = data[i * 4 + 3] ?? 0;
    return { alpha, widthPx, heightPx, naturalWidthPx, naturalHeightPx };
  } catch {
    // A file the browser cannot decode is design-validate's problem, not this step's: the operator
    // simply gets the whole-file default and the upload proceeds.
    return null;
  } finally {
    bitmap?.close();
  }
}
